import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { MdEmail, MdFileDownload } from 'react-icons/md';
import { Button } from 'react-covideo-common';
import { LoadingIndicator, Search, Table, Dropdown } from 'lib/components';
import { getLeaderboard, sendLeaderboard } from 'lib/api';
import { exportCSVFile, priceFormatter } from 'lib/utils/functions';

import {
  calculateDatesForRange,
  DateRangeSelector,
  SendReportModal,
  timeRangeConstants,
} from '../../components';
import {
  Rank,
  RoItems,
  CloseRatio,
  serviceExportTableCols,
  salesExportTableCols,
  serviceTableCols,
  salesTableCols,
  serviceColumnWidths,
  salesColumnWidths,
} from '../components';
import { useAuth } from 'lib/context';
import { QUOTE_SETTINGS } from 'app/pages/repairOrders/components/RepairOrderListing';
import {
  automotiveDepartmentOptions,
  AutomotiveDepartment,
} from 'lib/const/AutomotiveDepartment';
import { VideoTypeSales } from 'lib/const/VideoType';
import { Gap, HeaderWrapper } from 'lib/components/styles/layout';
import { Heading } from 'lib/components/styles/typography';
import { QuoteItem } from 'lib/api/repairOrders/types';
import { successToast } from 'lib/components/toasts/success';
import { errorToast } from 'lib/components/toasts/error';
import { getDateRangeFromLocalstorage } from '../../components/DateRangeSelector/DateRangeSelector';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';

type TableCellProps = {
  textAlign: string;
};
const TableCell = styled.div<TableCellProps>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: ${props => props.textAlign};
  padding-left: 10px;
`;

const EmptyList = styled.div`
  text-align: center;
  margin-top: 200px;
`;

type TableCol = {
  value: string;
  label: string;
  headerAlign?: string;
  valueAlign?: string;
  format?: string;
};

type LeaderboardItem = {
  firstName?: string;
  lastName?: string;
  roCount?: number;
  roiTotal?: number;
  roiApproved?: number;
  roiReq?: number;
  roiOpt?: number;
  total?: number;
  approved?: number;
  rank?: number;
  closeRatioDollars?: number;
  closeRatioItems?: number;
  quoteItems?: QuoteItem[];
  advisor?: string;
  recorded?: number;
  sent?: number;
  engagment?: number;
  marketing?: number | string;
  prospecting?: number | string;
  rti?: number | string;
  tyfb?: number | string;
  views?: number;
};

const sortByItems = (usersToSort: LeaderboardItem[]) => {
  return usersToSort.sort((a: LeaderboardItem, b: LeaderboardItem) => {
    if (a.closeRatioItems === b.closeRatioItems) {
      if (!b.roiApproved || !a.roiApproved) return 0;
      return b.roiApproved - a.roiApproved;
    }
    if (!b.closeRatioItems || !a.closeRatioItems) return 0;
    return b.closeRatioItems - a.closeRatioItems;
  });
};

const sortByDollar = (usersToSort: LeaderboardItem[]) => {
  return usersToSort.sort((a: LeaderboardItem, b: LeaderboardItem) => {
    if (a.closeRatioDollars === b.closeRatioDollars) {
      if (!b.approved || !a.approved) return 0;
      return b.approved - a.approved;
    }
    if (!b.closeRatioDollars || !a.closeRatioDollars) return 0;
    return b.closeRatioDollars - a.closeRatioDollars;
  });
};

const sortByVideos = (usersToSort: LeaderboardItem[]) => {
  return usersToSort.sort((a: LeaderboardItem, b: LeaderboardItem) => {
    if (a.recorded === b.recorded) {
      if (!b.sent || !a.sent) return 0;
      return b.sent - a.sent;
    }
    if (!b.recorded || !a.recorded) return 0;
    return b.recorded - a.recorded;
  });
};

const prepareDataForService = (data: any) => {
  let leaderboard: { [key: string]: any } = {};
  data.forEach((order: any) => {
    if (!leaderboard[order.advisor.id]) {
      leaderboard[order.advisor.id] = {
        quoteItems: order.quoteItems,
        roCount: 1,
        advisor: order.advisor,
      };
    } else {
      leaderboard[order.advisor.id] = {
        quoteItems: [
          ...leaderboard[order.advisor.id].quoteItems,
          ...order.quoteItems,
        ],
        roCount: leaderboard[order.advisor.id].roCount + 1,
        advisor: order.advisor,
      };
    }
  });
  const leaderboardData = Object.values(leaderboard).map(
    (leaderboardItem: any) => {
      const quoteItems = leaderboardItem.quoteItems;
      const roiTotal = quoteItems.length;
      const roiReq = quoteItems.filter((q: QuoteItem) => q.required).length;
      const roiApproved = quoteItems.filter(
        (q: QuoteItem) => q.approved
      ).length;
      const roiOpt = roiTotal - roiReq;
      const total = quoteItems.reduce(
        (sum: number, q: QuoteItem) => sum + (q.price ? Number(q.price) : 0),
        0
      );
      const approved = quoteItems
        .filter((q: QuoteItem) => q.approved)
        .reduce(
          (sum: number, q: QuoteItem) => sum + (q.price ? Number(q.price) : 0),
          0
        );
      const closeRatioDollars = total
        ? Math.round((approved / total) * 100)
        : 0;
      const closeRatioItems = roiTotal
        ? Math.round((roiApproved / roiTotal) * 100)
        : 0;
      return {
        ...leaderboardItem,
        roiTotal,
        roiApproved,
        roiReq,
        roiOpt,
        total,
        approved,
        closeRatioDollars,
        closeRatioItems,
        advisor:
          leaderboardItem.advisor.firstName +
          ' ' +
          leaderboardItem.advisor.lastName,
      };
    }
  );
  return leaderboardData;
};

const prepareDataForSales = (data: any) => {
  const leaderboardData = data.map((row: any) => {
    const advisor = row.name || '';
    const sent = row.sent || '-';
    const recorded = row.recorded || '-';
    const views = row.views || '-';
    const engagement = Math.round(row.engagments?.engagement) || 0;
    let marketing = 0;
    let prospecting = 0;
    let rti = 0;
    let tyfb = 0;
    row.videoTypes.forEach((videoType: string) => {
      if (videoType === VideoTypeSales.Marketing) ++marketing;
      if (videoType === VideoTypeSales.Prospecting) ++prospecting;
      if (videoType === VideoTypeSales.RespondToInquiry) ++rti;
      if (videoType === VideoTypeSales.ThanksForYourBusiness) ++tyfb;
    });
    return {
      advisor,
      sent,
      recorded,
      views,
      engagement,
      marketing,
      prospecting,
      rti,
      tyfb,
      duration: row.duration,
    };
  });
  return leaderboardData;
};

export const List = () => {
  const { userData } = useAuth();
  const { customer } = userData;
  const [department, setDepartment] = useState(
    userData.isAutomotiveServiceRole
      ? AutomotiveDepartment.SERVICE
      : AutomotiveDepartment.SALES
  );
  const location = useLocation();
  const queryParams: any = queryString.parse(location.search);

  const storedDateRange = getDateRangeFromLocalstorage();
  const initRange =
    queryParams.range ||
    storedDateRange?.range ||
    timeRangeConstants.LAST_7_DAYS;
  const { start, end } = calculateDatesForRange(
    initRange,
    storedDateRange?.start,
    storedDateRange?.end
  );

  const [dateRange, setDateRange] = useState<{
    start: Date;
    end: Date;
    range: string;
  }>({
    start,
    end,
    range: initRange,
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([] as LeaderboardItem[]);
  const [filteredUsers, setFilteredUsers] = useState([] as LeaderboardItem[]);
  const [tableCols, setTableCols] = useState<TableCol[]>([]);
  const [exportTableCols, setExportTableCols] = useState<TableCol[]>([]);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);

  const preferItem = customer.quoteSettings === QUOTE_SETTINGS.ITEM;
  const preferDollar = customer.quoteSettings === QUOTE_SETTINGS.DOLLAR;

  useEffect(() => {
    if (department === AutomotiveDepartment.SERVICE) {
      setTableCols(serviceTableCols);
      setExportTableCols(serviceExportTableCols);
      setColumnWidths(serviceColumnWidths);
    }
    if (department === AutomotiveDepartment.SALES) {
      setTableCols(salesTableCols);
      setExportTableCols(salesExportTableCols);
      setColumnWidths(salesColumnWidths);
    }
  }, [department]);

  useEffect(() => {
    refreshUsers();
  }, [dateRange, department]);

  useEffect(() => {
    setFilteredUsers(search.length ? searchUsers(users) : users);
  }, [users, search]);

  const refreshUsers = async () => {
    setLoading(true);
    try {
      const data = await getLeaderboard({
        startDate: dateRange.start,
        endDate: dateRange.end,
        department,
      });
      const isService = department === AutomotiveDepartment.SERVICE;
      const leaderboardData = isService
        ? prepareDataForService(data)
        : prepareDataForSales(data);
      const sortedUsers = isService
        ? sortUsersForService(leaderboardData)
        : sortUsersForSales(leaderboardData);
      const rankedUsers = isService
        ? rankUsersForService(sortedUsers)
        : rankUsersForSales(sortedUsers);
      setUsers(rankedUsers);
    } catch (error) {
      console.log('error', error);
    }
    setLoading(false);
  };

  const sortUsersForService = (usersToSort: LeaderboardItem[]) => {
    const sortedUsers =
      customer.quoteSettings && preferItem
        ? sortByItems(usersToSort)
        : sortByDollar(usersToSort);
    return sortedUsers;
  };

  const sortUsersForSales = (usersToSort: LeaderboardItem[]) => {
    const sortedUsers = sortByVideos(usersToSort);
    return sortedUsers;
  };

  const rankUsersForService = (usersToRank: LeaderboardItem[]) => {
    let lastSharedRank = 0;
    const rankedUsers = usersToRank.map(
      (user: LeaderboardItem, index: number) => {
        const prev = usersToRank[index - 1];
        const itemRankShared =
          !!prev &&
          user.closeRatioItems === prev.closeRatioItems &&
          user.roiApproved === prev.roiApproved;
        const dollarRankShared =
          !!prev &&
          user.closeRatioDollars === prev.closeRatioDollars &&
          user.approved === prev.approved;
        let rank = index + 1;
        if (
          (preferItem && itemRankShared) ||
          (preferDollar && dollarRankShared)
        ) {
          rank = lastSharedRank ? lastSharedRank : index;
          lastSharedRank = rank;
        } else {
          lastSharedRank = 0;
        }
        return { ...user, rank };
      }
    );
    return rankedUsers;
  };

  const rankUsersForSales = (usersToRank: LeaderboardItem[]) => {
    let lastSharedRank = 0;
    const rankedUsers = usersToRank.map(
      (user: LeaderboardItem, index: number) => {
        const prev = usersToRank[index - 1];
        const rankShared =
          !!prev && user.sent === prev.sent && user.recorded === prev.recorded;
        let rank = index + 1;
        if (rankShared) {
          rank = lastSharedRank ? lastSharedRank : index;
          lastSharedRank = rank;
        } else {
          lastSharedRank = 0;
        }
        return { ...user, rank };
      }
    );
    return rankedUsers;
  };

  const onSearch = (query: string) => {
    query = query.toLowerCase();
    setSearch(query.toLowerCase());
  };

  const searchUsers = (usersToFilter: LeaderboardItem[]) => {
    const filteredUsers = usersToFilter.filter((li: LeaderboardItem) => {
      return li.advisor?.toLowerCase().includes(search);
    });
    return filteredUsers;
  };

  const onDateRangeChange = (start: Date, end: Date, range: string) => {
    setDateRange({ start, end, range });
  };

  const prepareExportData = async () => {
    if (!filteredUsers) {
      return { exportHeader: null, exportDataList: null };
    }

    let exportHeader: { [key: string]: string } = {};
    exportTableCols.forEach(field => {
      exportHeader[field.value] = field.label;
    });

    let exportDataList: {
      [key: string]: string | number | QuoteItem[] | undefined;
    }[] = [];

    filteredUsers.forEach((filteredUser: LeaderboardItem) => {
      const exportData: {
        [key: string]: string | number | QuoteItem[] | undefined;
      } = {};
      filteredUser.marketing = filteredUser.marketing || '-';
      filteredUser.prospecting = filteredUser.prospecting || '-';
      filteredUser.rti = filteredUser.rti || '-';
      filteredUser.tyfb = filteredUser.tyfb || '-';

      exportTableCols.forEach((field: TableCol) => {
        let value = filteredUser[field.value as keyof LeaderboardItem];
        if (field.format && field.format === 'price') {
          if (typeof value === 'number') {
            value = priceFormatter(value ?? 0, false);
          }
        }

        if (field.format && field.format === 'percent') {
          value = `${value ?? 0}%`;
        }

        if (field.value === 'closeRatio') {
          value =
            !customer.quoteSettings ||
            customer.quoteSettings === QUOTE_SETTINGS.DOLLAR
              ? `${filteredUser.closeRatioDollars || 0}%`
              : `${filteredUser.closeRatioItems || 0}%`;
        }

        exportData[field.value] = value ?? 0;
      });

      exportDataList.push(exportData);
    });

    return { exportHeader, exportDataList };
  };

  const downloadAll = async () => {
    const { exportHeader, exportDataList } = await prepareExportData();
    if (!exportHeader || !exportDataList) return;

    exportCSVFile(exportHeader, exportDataList, 'leaderboard');
  };

  const sendData = async (recipients: string[]) => {
    const { exportHeader, exportDataList } = await prepareExportData();
    if (!exportHeader || !exportDataList) return;
    try {
      await sendLeaderboard({
        data: {
          headers: exportHeader,
          csvData: exportDataList,
        },
        recipients,
      });
      successToast({ title: 'Successfully sent report' });
    } catch (err) {
      errorToast({ title: 'Failed to send report' });
    }
  };

  return (
    <>
      <Heading m='0 0 16px 0'>Leaderboard</Heading>
      <HeaderWrapper style={{ gap: '20px' }}>
        <Search
          width='240px'
          placeholder='Search users...'
          onSearch={onSearch}
        />
        <Gap gap={'10px'}>
          <DateRangeSelector
            onDateRangeChange={onDateRangeChange}
            onTodayOnlyDate={true}
            reverse={true}
            dropdownWidth={'150px'}
            initialValue={dateRange.range}
          />
          <Dropdown
            width={145}
            value={automotiveDepartmentOptions.find(o => {
              return o.value === department;
            })}
            options={automotiveDepartmentOptions}
            onChange={(option: any) => setDepartment(option.value)}
          />
          <Button
            variant='primary'
            text='Send'
            icon={<MdEmail />}
            onClick={() => setIsSendModalOpen(true)}
          />
          <Button
            variant='primary'
            text='Download'
            icon={<MdFileDownload />}
            onClick={() => downloadAll()}
          />
        </Gap>
      </HeaderWrapper>
      <>
        {loading && <LoadingIndicator isLoading={loading} height='300px' />}
        {!loading && (
          <>
            {!!filteredUsers.length && (
              <Table
                compact={true}
                columnWidths={columnWidths}
                headers={[
                  ...tableCols.map(item => {
                    return (
                      <TableCell textAlign={item.headerAlign || 'left'}>
                        {item.label}
                      </TableCell>
                    );
                  }),
                ]}
                hoverable={false}
                rows={filteredUsers.map((user: any, index: number) => ({
                  key: index,
                  columns: [
                    ...tableCols.map(item => {
                      if (item.value === 'rank') {
                        return (
                          <TableCell textAlign={item.valueAlign || 'left'}>
                            <Rank rank={user[item.value]} />
                          </TableCell>
                        );
                      }
                      if (item.value === 'roItems') {
                        return (
                          <TableCell textAlign={item.valueAlign || 'left'}>
                            <RoItems
                              roiTotal={user.roiTotal || 0}
                              roiReq={user.roiReq || 0}
                              roiOpt={user.roiOpt || 0}
                            />
                          </TableCell>
                        );
                      }
                      if (item.value === 'closeRatio') {
                        return (
                          <TableCell textAlign={item.valueAlign || 'left'}>
                            <CloseRatio
                              quoteSettings={customer.quoteSettings || ''}
                              closeRatioItems={user.closeRatioItems || 0}
                              closeRatioDollars={user.closeRatioDollars || 0}
                            />
                          </TableCell>
                        );
                      }
                      if (item.format === 'price') {
                        return (
                          <TableCell textAlign={item.valueAlign || 'left'}>
                            {priceFormatter(user[item.value] || 0)}
                          </TableCell>
                        );
                      }
                      if (item.format === 'percent') {
                        return (
                          <TableCell textAlign={item.valueAlign || 'left'}>
                            {user[item.value] || '0'}%
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell textAlign={item.valueAlign || 'left'}>
                          {user[item.value] || '-'}
                        </TableCell>
                      );
                    }),
                  ],
                }))}
                cellHeight='80px'
              />
            )}
            {!filteredUsers.length && <EmptyList>No data to show.</EmptyList>}
          </>
        )}
      </>
      {isSendModalOpen && (
        <SendReportModal
          handleModalClose={() => setIsSendModalOpen(false)}
          startDate={new Date()}
          endDate={new Date()}
          range={''}
          sendDataFunction={sendData}
          skipCreateRecurring={true}
        />
      )}
    </>
  );
};
